import { z } from 'zod';
import { WorkflowDataSubmissionSchema } from './workflow-submission';
// Zod schemas for validation
/**
 * Schema for archive metadata
 */
export const ArchiveMetadataSchema = z.object({
    archived_at: z.object({
        seconds: z.number(),
        nanoseconds: z.number(),
        toDate: z.function(),
        toMillis: z.function(),
    }),
    archived_by: z.string().min(1),
    archived_by_name: z.string().min(1),
    archive_reason: z.string().optional(),
    original_cycle_id: z.string().min(1),
    modification_count: z.number().min(0),
    last_modified_at: z.object({
        seconds: z.number(),
        nanoseconds: z.number(),
        toDate: z.function(),
        toMillis: z.function(),
    }).optional(),
    last_modified_by: z.string().optional(),
    last_modified_by_name: z.string().optional(),
});
/**
 * Schema for modification history entry
 */
export const ModificationHistoryEntrySchema = z.object({
    modified_at: z.object({
        seconds: z.number(),
        nanoseconds: z.number(),
        toDate: z.function(),
        toMillis: z.function(),
    }),
    modified_by: z.string().min(1),
    modified_by_name: z.string().min(1),
    fields_changed: z.array(z.string()),
    modification_reason: z.string().optional(),
    previous_values: z.record(z.string(), z.any()).optional(),
});
/**
 * Schema for archived data submission
 */
export const ArchivedDataSubmissionSchema = WorkflowDataSubmissionSchema.omit({ id: true }).extend({
    id: z.string().min(1),
    archive_metadata: ArchiveMetadataSchema,
    modification_history: z.array(ModificationHistoryEntrySchema),
    editable: z.boolean(),
    tags: z.array(z.string()).optional(),
    archive_notes: z.string().optional(),
});
/**
 * Schema for creating archived submission
 */
export const CreateArchivedSubmissionDataSchema = z.object({
    original_submission: WorkflowDataSubmissionSchema,
    archive_reason: z.string().optional(),
    archive_notes: z.string().optional(),
    tags: z.array(z.string()).optional(),
});
/**
 * Schema for updating archived submission
 */
export const UpdateArchivedSubmissionDataSchema = z.object({
    fte_input: z.array(z.object({
        user_id: z.string().min(1),
        fte_amount: z.number().min(0).max(100),
        last_updated: z.object({
            seconds: z.number(),
            nanoseconds: z.number(),
            toDate: z.function(),
            toMillis: z.function(),
        }).optional(),
        updated_by: z.string().optional(),
    })).optional(),
    relationship_input: z.array(z.object({
        question_id: z.string().min(1),
        response: z.enum(['yes', 'no']),
        last_updated: z.object({
            seconds: z.number(),
            nanoseconds: z.number(),
            toDate: z.function(),
            toMillis: z.function(),
        }).optional(),
        updated_by: z.string().optional(),
    })).optional(),
    objective_input: z.array(z.object({
        objective: z.enum(['roas', 'cpa', 'revenue', 'conversions']),
        objective_type: z.enum(['efficiency', 'volume']),
        objective_goal: z.number(),
        objective_result: z.number().optional(),
        last_updated: z.object({
            seconds: z.number(),
            nanoseconds: z.number(),
            toDate: z.function(),
            toMillis: z.function(),
        }).optional(),
        updated_by: z.string().optional(),
    })).optional(),
    spend_input: z.object({
        entries: z.array(z.object({
            deal_id: z.string().min(1),
            deal_name: z.string().min(1),
            description: z.string(),
            entries: z.string(),
            channel: z.string().min(1),
            tactic: z.string().min(1),
            currency: z.string().min(1),
            amount: z.number().min(0),
            last_updated: z.object({
                seconds: z.number(),
                nanoseconds: z.number(),
                toDate: z.function(),
                toMillis: z.function(),
            }).optional(),
            updated_by: z.string().optional(),
        })),
        total_spend: z.number().optional(),
        primary_currency: z.string().optional(),
    }).optional(),
    spend_fluctuations: z.object({
        context: z.string().min(1),
        last_updated: z.object({
            seconds: z.number(),
            nanoseconds: z.number(),
            toDate: z.function(),
            toMillis: z.function(),
        }).optional(),
        updated_by: z.string().optional(),
    }).optional(),
    archive_notes: z.string().optional(),
    tags: z.array(z.string()).optional(),
    modification_reason: z.string().optional(),
});
/**
 * Schema for archive filter criteria
 */
export const ArchivedSubmissionFilterSchema = z.object({
    original_cycle_id: z.string().optional(),
    cycle_id: z.string().optional(),
    workflow_id: z.string().optional(),
    workflow_name: z.string().optional(),
    archived_after: z.object({
        seconds: z.number(),
        nanoseconds: z.number(),
        toDate: z.function(),
        toMillis: z.function(),
    }).optional(),
    archived_before: z.object({
        seconds: z.number(),
        nanoseconds: z.number(),
        toDate: z.function(),
        toMillis: z.function(),
    }).optional(),
    archived_by: z.string().optional(),
    tags: z.array(z.string()).optional(),
    editable: z.boolean().optional(),
});
// Type guards
export const isValidArchivedDataSubmission = (data) => {
    return ArchivedDataSubmissionSchema.safeParse(data).success;
};
export const isValidCreateArchivedSubmissionData = (data) => {
    return CreateArchivedSubmissionDataSchema.safeParse(data).success;
};
export const isValidUpdateArchivedSubmissionData = (data) => {
    return UpdateArchivedSubmissionDataSchema.safeParse(data).success;
};
export const isValidArchivedSubmissionFilter = (data) => {
    return ArchivedSubmissionFilterSchema.safeParse(data).success;
};
export const isValidArchiveMetadata = (data) => {
    return ArchiveMetadataSchema.safeParse(data).success;
};
export const isValidModificationHistoryEntry = (data) => {
    return ModificationHistoryEntrySchema.safeParse(data).success;
};
